import { FabricWorld } from '@/commons/fabricjs/FabricWorld';
import {
    objectMovingConstrainCallback,
    onUpdateObjectCallback,
    onUpdateTextBoxCallback,
    onOneTextBoxSelectedCallback,
    onFabricCanvasScaleCallback,
    setCuttingPathVisibility,
} from '@/commons/fabricjs/FabricCallbackHelper';
import {
    onSelectContextualMenu,
    onTextChangeContextualMenu,
    onMouseWheelContextualMenu,
    onMoveContextualMenu,
} from '@/commons/fabricjs/FabricContextualMenuController';
import { debounce } from '@/utils/debounce';
import {
    IMAGE_PX_WIDTH_33,
    IMAGE_PX_HEIGHT_33,
    IMAGE_PX_WIDTH_50,
    IMAGE_PX_HEIGHT_50,
    GRADUATION_NAME,
    CUTTING_PATH_DEFAULT_OPACITY,
    CUTTING_PATH_SELECTION_OPACITY,
    BACKGROUND_MASK_DEFAULT_OPACITY,
    BACKGROUND_MASK_SELECTION_OPACITY,
} from '@/littleWedding/projectConstantes';
import { BACKGROUND_MASK_NAME, CUTTING_GROUP_NAME, GRID_HELPER, WORKING_ZONE_NAME } from '@/littleWedding/fabricGroupsName';
import { initClickCallback, initPanCallback } from '@/commons/fabricjs/FabricPanCallback';
import { initZoomCallback } from '@/commons/fabricjs/FabricZoomCallback';
import { EGobeletType } from '@/littleWedding/EGobeletType';
import { Events } from '@/services/Events';
import { ImageLoaderManager } from '@/commons/assetsManager/ImageLoaderManager';
import { getFabricGroupById } from '@/commons/fabricjs/FabricGroupHelper';
import { CANVAS_ZOOM_MAX } from '@/config';
import { buildMaskPath, setBackgroundMaskVisibility } from '@/commons/fabricjs/FabricBackgroundMaskHelper';
import { Rect, Group, IText, FabricImage, type BasicTransformEvent, FabricObject, Point } from 'fabric';
import type { IFabricWorldInitParams } from '@/commons/fabricjs/IFabricWorldInitParams';
import type { IFabricTextCustom } from '@/typescript/interfaces/IFabricTextCustom';
import type { IFabricImageCustom } from '@/typescript/interfaces/IFabricImageCustom';
import type { ITextboxCustom } from '@/typescript/interfaces/ITextboxCustom';
import type { IGroup } from '@/typescript/interfaces/IGroup';
import type { IFabricCustomSelectable } from '@/typescript/interfaces/TFabricCustomSelectable';

// Minimum visualViewport height loss (px) to consider the phone keyboard open; Safari toolbar
// collapsing shrinks the viewport by far less than any soft keyboard
const KEYBOARD_MIN_HEIGHT = 150;

class WFabricWorld extends FabricWorld {
    // #region SETUP
    zoomFactorMax: number = 5; // max zoom factor
    zoomFactorMin: number = 0;
    private width: number = 0; // unity in pixels including DPI calculation
    private height: number = 0; // unity in pixels including DPI calculation
    updateObjectCallback = debounce(onUpdateObjectCallback, 100);
    objectMovingConstrain: (options: BasicTransformEvent & { target: FabricObject }) => void;
    nativeTouchHandler?: (event: TouchEvent) => void;
    phoneKeyboardHandler?: (event: Event) => void;
    // visualViewport height measured at init, used to tell a keyboard open/close apart from other resizes
    private initialViewportHeight: number = 0;
    // onBeforeTransform;
    smoothImages: boolean = true;
    productType: string | EGobeletType = EGobeletType.SMALL;
    objectConstrainId: string[] = [];
    isInitialized: boolean = false;

    onSelect: () => void;
    onDeselect: () => void;

    constructor() {
        super();
        this.onSelect = this.setMaskTransparent.bind(this);
        this.onDeselect = this.setMaskOpaque.bind(this);
    }

    init(params: IFabricWorldInitParams, guides = true) {
        this.smoothImages = params.smoothImages ?? true;
        this.productType = params.productType;
        this.initSizes(params);

        // delete width and height from params to avoid resize of DOM canvas
        delete params.width;
        delete params.height;

        super.init(params);
        this.fabricCanvas?.set('backgroundColor', params.parentBackgroundColor);
        if (guides) {
            this.addWorkingZone();
        }

        this.fabricCanvas?.renderAll();
        this.initListeners();
        this.isInitialized = true;
    }

    setMaskOpaque() {
        if (!this.fabricCanvas) return;
        setCuttingPathVisibility(this.fabricCanvas, CUTTING_PATH_DEFAULT_OPACITY);
        setBackgroundMaskVisibility(this.fabricCanvas, BACKGROUND_MASK_DEFAULT_OPACITY);
    }

    setMaskTransparent() {
        if (!this.fabricCanvas) return;
        setCuttingPathVisibility(this.fabricCanvas, CUTTING_PATH_SELECTION_OPACITY);
        setBackgroundMaskVisibility(this.fabricCanvas, BACKGROUND_MASK_SELECTION_OPACITY);
    }

    /**
     * Initialize the working size of the fabric world
     * @remarks This method sets the width and height of the fabric world based on the provided parameters.
     * If the product type is 'cup_33' or 'cup_50', it uses predefined dimensions.
     * Otherwise, it uses the width and height from the parameters or defaults to
     * the predefined dimensions.
     * @todo refactor this function in the futur if cup value is provide by the config and not hard coded
     * @depreciated will be removed when gobelet size will comme from the backoffice
     * @param params IFabricWorldInitParams to get width and height if defined
     */
    initSizes(params: IFabricWorldInitParams) {
        const defaultWidth = this.productType === EGobeletType.BIG ? IMAGE_PX_WIDTH_50 : IMAGE_PX_WIDTH_33;
        const defaultHeight = this.productType === EGobeletType.BIG ? IMAGE_PX_HEIGHT_50 : IMAGE_PX_HEIGHT_33;
        // TODO: remove when 3d model will be use directly from backoffice
        if (Object.values(EGobeletType).includes(this.productType as EGobeletType)) {
            this.width = defaultWidth;
            this.height = defaultHeight;
        } else {
            // still applying fallback values just in case
            this.width = params.width || defaultWidth;
            this.height = params.height || defaultHeight;
        }
    }

    get imageRenderWidth() {
        return this.width;
    }

    get imageRenderHeight() {
        return this.height;
    }

    /**
     * usefull to get delta between real final image
     */
    get scaleFactor() {
        const offset = 500;
        return Math.min(this.canvasWidth / (this.imageRenderWidth + offset), this.canvasHeight / (this.imageRenderHeight + offset));
    }

    removeFabricElementById(id: string) {
        const object = this.getFabricElementById(id);
        if (object) {
            object.hasControls = false;
            this.fabricCanvas?.renderAll();
            this.fabricCanvas?.remove(object);
            object.dispose();
        }
        this.objectConstrainId = this.objectConstrainId.filter((objectId) => objectId !== id);
    }

    setClickedActiveObject = (object: IFabricCustomSelectable | IGroup) => {
        Events.emit('field-selected', (object as IFabricCustomSelectable)?.id);
    };

    textChangedCallback = (object: { target: IText }) => {
        if (object.target.type !== 'textbox') {
            return;
        }
        onUpdateTextBoxCallback(object.target as ITextboxCustom);
    };

    // #endregion
    // #region GUIDES Methods
    addWorkingZone() {
        const workingZoneGroup = new Group([], {
            selectable: false,
            evented: false,
        });
        workingZoneGroup.set('objectCaching', false);
        workingZoneGroup.set('name', WORKING_ZONE_NAME);

        const contour = new Rect({
            width: Math.floor(this.imageRenderWidth),
            height: Math.floor(this.imageRenderHeight),
            fill: 'white',
            strokeWidth: 0,
            left: 0,
            top: 0,
        });

        contour.set('objectCaching', false);
        workingZoneGroup.add(contour);
        contour.set('top', Math.floor(contour.top));
        contour.set('left', Math.floor(contour.left));
        this.fabricCanvas?.add(workingZoneGroup);

        const maskColor = this.fabricCanvas?.backgroundColor || 'rgba(0,0,0,0.5)';
        const mask = buildMaskPath(contour, { fill: maskColor });
        mask.set('id', BACKGROUND_MASK_NAME);
        mask.set('top', Math.floor(mask.top + this.imageRenderHeight / 2));
        mask.set('left', Math.floor(mask.left + this.imageRenderWidth / 2));
        this.fabricCanvas?.add(mask);
        this.fabricCanvas?.bringObjectToFront(mask);
    }

    setGridHelperVisibility(isVisible: boolean) {
        if (!this.fabricCanvas) {
            return;
        }
        const gridGroupHelper = getFabricGroupById(this.fabricCanvas, GRID_HELPER);
        if (!gridGroupHelper) {
            return;
        }
        gridGroupHelper.set('visible', isVisible);
        this.fabricCanvas?.renderAll();
    }

    deleteGridHelper() {
        if (!this.fabricCanvas) {
            return;
        }
        this.removeFabricElementById(GRID_HELPER);
    }

    deleteCuttingPath() {
        if (!this.fabricCanvas) {
            return;
        }
        this.removeFabricElementById(CUTTING_GROUP_NAME);
    }

    async createGridHelper(gridUrl: string, isProductEdition: boolean) {
        return new Promise((resolve, reject) => {
            ImageLoaderManager.loadImage(gridUrl)
                .then((imageLoaded) => {
                    if (imageLoaded instanceof Error || this.fabricCanvas === null) {
                        console.error('Error loading image:', imageLoaded);
                        return;
                    }
                    const oldGridGroup = getFabricGroupById(this.fabricCanvas, GRID_HELPER);
                    if (oldGridGroup) {
                        this.fabricCanvas?.remove(oldGridGroup);
                    }
                    const foreGroundGroup = new Group([], {
                        selectable: isProductEdition, // allow to select grid only in product edition mode
                        evented: isProductEdition,
                        visible: false,
                        lockMovementX: true,
                        lockMovementY: true,
                        lockScalingX: true,
                        lockScalingY: true,
                        lockRotation: true,
                    });

                    foreGroundGroup.set('id', GRID_HELPER);
                    const gridImage = new FabricImage(imageLoaded, {
                        // width: this.imageRenderWidth,
                        // height: this.imageRenderHeight,
                        left: this.imageRenderWidth / 2,
                        top: this.imageRenderHeight / 2,
                        originX: 'center',
                        originY: 'center',
                        opacity: 0.5,
                        selectable: isProductEdition,
                        evented: isProductEdition,
                        imageSmoothing: true,
                        lockMovementX: true,
                        lockMovementY: true,
                        lockScalingX: true,
                        lockScalingY: true,
                        lockRotation: true,
                        id: 'gridHelper',
                    });
                    foreGroundGroup.add(gridImage);

                    const workingZone = this.fabricCanvas.getObjects().find((obj) => obj.get('name') === WORKING_ZONE_NAME);
                    if (workingZone && this.fabricCanvas) {
                        // place grid just above working zone
                        const workingZoneIndex = this.fabricCanvas.getObjects().indexOf(workingZone);
                        this.fabricCanvas.insertAt(workingZoneIndex + 1, foreGroundGroup);
                    } else {
                        // add grid at the bottom of the stack
                        this.fabricCanvas.add(foreGroundGroup);
                        this.fabricCanvas.sendObjectToBack(foreGroundGroup);
                    }

                    this.fabricCanvas.renderAll();
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    // #endregion
    // #region HELPERS Methods
    displayImageFullCanvas() {
        const offsetFactor = 1.3;
        const imgScaleFactor = Math.min(
            this.canvasWidth / (this.imageRenderWidth * offsetFactor),
            this.canvasHeight / (this.imageRenderHeight * offsetFactor),
        );
        this.zoomFactorMin = imgScaleFactor;
        this.fabricCanvas?.setZoom(imgScaleFactor);
        const canvasZoom = this.fabricCanvas?.getZoom() ?? 1;

        const [zoom0, number1, number2, zoom3] = this.fabricCanvas!.viewportTransform;
        this.fabricCanvas?.setViewportTransform([
            zoom0,
            number1,
            number2,
            zoom3,
            this.canvasWidth / 2 - (this.imageRenderWidth / 2) * canvasZoom,
            this.canvasHeight / 2 - (this.imageRenderHeight / 2) * canvasZoom,
        ]);

        this.fabricCanvas?.renderAll();
    }

    displayRealSizeCanvas() {
        this.fabricCanvas?.setZoom(1);
        const [zoom0, number1, number2, zoom3] = this.fabricCanvas!.viewportTransform;
        this.fabricCanvas?.setViewportTransform([zoom0, number1, number2, zoom3, 0, 0]);
    }

    getFabricElementById(id: string): IFabricCustomSelectable {
        return this.fabricCanvas?.getObjects().find((obj) => {
            if ((obj as IFabricImageCustom).id === id) {
                return obj as IFabricImageCustom;
            }
            if ((obj as IFabricTextCustom).id === id) {
                return obj as IFabricTextCustom;
            }
            if ((obj as ITextboxCustom).id === id) {
                return obj as ITextboxCustom;
            }
            return null;
        }) as IFabricCustomSelectable;
    }

    zoomInOut(zoomFactor: number, minZoom: number = 0.1, maxZoom: number = CANVAS_ZOOM_MAX) {
        const zoomCenter = this.fabricCanvas?.getCenterPoint();
        if (zoomFactor >= minZoom && zoomFactor <= maxZoom && zoomCenter) {
            this.fabricCanvas?.zoomToPoint(zoomCenter, zoomFactor);
        }
    }

    keepGraduationImageUpFront() {
        const graduationImage = this.fabricCanvas?.getObjects('image').find((img) => Object.values(img).includes(GRADUATION_NAME));
        if (graduationImage) {
            this.fabricCanvas?.bringObjectToFront(graduationImage);
            this.fabricCanvas?.renderAll();
        }
    }

    keepGridImageUpFront() {
        const gridImage = this.getFabricElementById(GRID_HELPER);
        if (gridImage) {
            this.fabricCanvas?.bringObjectToFront(gridImage);
            this.fabricCanvas?.renderAll();
        }
    }

    keepBackgroundMaskUpFront() {
        const mask = this.getFabricElementById(BACKGROUND_MASK_NAME);
        if (mask) {
            this.fabricCanvas?.bringObjectToFront(mask);
            this.fabricCanvas?.renderAll();
        }
    }

    initListeners() {
        const zoomCallback = initZoomCallback(this.fabricCanvas, this.zoomFactorMin);
        if (zoomCallback instanceof Error) {
            throw zoomCallback;
        } else {
            this.zoomCallback = zoomCallback;
            this.fabricCanvas?.on('mouse:wheel', this.zoomCallback);
        }
        const clickCallback = initClickCallback(this.fabricCanvas, this.setClickedRefPoint, this.setClickedActiveObject);
        if (clickCallback instanceof Error) {
            throw clickCallback;
        } else {
            this.clickCallback = clickCallback;
            this.fabricCanvas?.on('mouse:down', this.clickCallback);
            this.fabricCanvas?.on('mouse:up', this.clickCallback);
        }

        const panCallback = initPanCallback(
            this.fabricCanvas,
            this.imageMargin,
            this.imageRenderHeight,
            this.scaleFactor,
            this.getClickedRefPoint,
            this.setClickedRefPoint,
            this.zoomFactorMin,
            this.zoomFactorMax,
        );

        if (panCallback instanceof Error) {
            throw panCallback;
        }

        this.panCallback = panCallback;
        this.fabricCanvas?.on('mouse:move', this.panCallback);

        // Add native touch event listeners for two-finger gestures
        const canvasElement = this.fabricCanvas?.getElement();
        if (canvasElement) {
            // Create wrapper for native touch events that feeds into our pan callback
            this.nativeTouchHandler = (event: TouchEvent) => {
                if (event.touches.length === 2) {
                    // Create a mock TPointerEventInfo for two-finger gestures
                    const mockOpt = {
                        e: event,
                        viewportPoint: new Point(0, 0),
                        absolutePointer: new Point(0, 0),
                        pointer: new Point(0, 0),
                    };
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    this.panCallback!(mockOpt as any);
                }
            };

            canvasElement.addEventListener('touchstart', this.nativeTouchHandler, { passive: false });
            canvasElement.addEventListener('touchmove', this.nativeTouchHandler, { passive: false });
            canvasElement.addEventListener('touchend', this.nativeTouchHandler, { passive: false });

            // init resize function to new viewport size when phone keyboard is shown
            const { height, width } = window.visualViewport || { width: 0, height: 0 };
            this.initialViewportHeight = height;
            this.phoneKeyboardHandler = () => {
                this.resize(width, height);
            };
            if (width < 769) {
                window.visualViewport?.addEventListener('resize', this.phoneKeyboardHandler);
            }
        }

        this.objectMovingConstrain = objectMovingConstrainCallback(this, {
            tl: new Point(),
            br: new Point({ x: this.imageRenderWidth, y: this.imageRenderHeight }),
        });
        // Update canvas Element
        this.fabricCanvas?.on('object:modified', this.updateObjectCallback);
        this.fabricCanvas?.on('object:moving', this.objectMovingConstrain);
        this.fabricCanvas?.on('object:added', this.updateObjectCallback);
        this.fabricCanvas?.on('text:changed', this.textChangedCallback);

        // contextual menu
        this.fabricCanvas?.on('object:moving', onMoveContextualMenu);
        this.fabricCanvas?.on('object:scaling', onMoveContextualMenu);
        this.fabricCanvas?.on('object:rotating', onMoveContextualMenu);

        this.fabricCanvas?.on('selection:created', this.onSelect);
        this.fabricCanvas?.on('selection:created', onSelectContextualMenu);
        this.fabricCanvas?.on('selection:created', onOneTextBoxSelectedCallback);
        this.fabricCanvas?.on('selection:updated', onSelectContextualMenu);
        this.fabricCanvas?.on('selection:updated', onOneTextBoxSelectedCallback);
        this.fabricCanvas?.on('selection:cleared', this.onDeselect);

        this.fabricCanvas?.on('text:changed', onTextChangeContextualMenu);
        this.fabricCanvas?.on('object:scaling', onFabricCanvasScaleCallback);
        this.fabricCanvas?.on('mouse:wheel', onMouseWheelContextualMenu);
    }

    dispose() {
        this.fabricCanvas?.off('object:modified', this.updateObjectCallback);
        this.fabricCanvas?.off('object:moving', this.objectMovingConstrain);
        this.fabricCanvas?.off('object:added', this.updateObjectCallback);
        this.fabricCanvas?.off('text:changed', this.textChangedCallback);

        this.fabricCanvas?.off('object:moving', onMoveContextualMenu);
        this.fabricCanvas?.off('object:scaling', onMoveContextualMenu);
        this.fabricCanvas?.off('object:rotating', onMoveContextualMenu);

        this.fabricCanvas?.off('selection:created', this.onSelect);
        this.fabricCanvas?.off('selection:created', onSelectContextualMenu);
        this.fabricCanvas?.off('selection:created', onOneTextBoxSelectedCallback);
        this.fabricCanvas?.off('selection:updated', onSelectContextualMenu);
        this.fabricCanvas?.off('selection:updated', onOneTextBoxSelectedCallback);
        this.fabricCanvas?.off('selection:cleared', this.onDeselect);
        this.fabricCanvas?.off('text:changed', onTextChangeContextualMenu);
        this.fabricCanvas?.off('object:scaling', onFabricCanvasScaleCallback);
        this.fabricCanvas?.off('mouse:wheel', onMouseWheelContextualMenu);

        // Clean up native touch event listeners
        if (this.nativeTouchHandler) {
            const canvasElement = this.fabricCanvas?.getElement();
            if (canvasElement) {
                canvasElement.removeEventListener('touchstart', this.nativeTouchHandler);
                canvasElement.removeEventListener('touchmove', this.nativeTouchHandler);
                canvasElement.removeEventListener('touchend', this.nativeTouchHandler);
            }
            if (this.phoneKeyboardHandler) {
                window.visualViewport?.removeEventListener('resize', this.phoneKeyboardHandler);
            }
        }

        super.dispose();
    }

    // #endregion
    focusOnElement(elementId: string) {
        const element = this.getFabricElementById(elementId);
        if (!element) {
            return;
        }
        const { x, y } = element.getCenterPoint();
        const bBox = element.getBoundingRect();
        const calcScalefactor = (bBox.width > bBox.height ? this.canvasWidth / bBox.width : this.canvasHeight / bBox.height) * 0.5;
        const scaleFactor = Math.min(calcScalefactor, 0.7);
        this.fabricCanvas?.setViewportTransform([
            scaleFactor,
            this.fabricCanvas!.viewportTransform[1],
            this.fabricCanvas!.viewportTransform[2],
            scaleFactor,
            this.canvasWidth / 2 - x * scaleFactor,
            this.canvasHeight / 2 - y * scaleFactor,
        ]);
        this.fabricCanvas?.renderAll();
    }

    /**
     * Resize the Fabric canvas to new dimensions and if on element is selected, focus on it
     * @param width - New width in pixels
     * @param height - New height in pixels
     * @param maintainViewport - Whether to maintain current viewport position and zoom
     */
    resize(width: number, height: number) {
        if (!this.fabricCanvas) {
            console.warn('Canvas not initialized');
            return;
        }

        // Update internal dimensions
        this.canvasWidth = width;
        this.canvasHeight = height;

        // Resize the canvas
        this.fabricCanvas.setDimensions({
            width: width,
            height: height,
        });

        // Update the canvas element dimensions
        const canvasElement = this.fabricCanvas.getElement();
        if (canvasElement) {
            canvasElement.style.width = `${width}px`;
            canvasElement.style.height = `${height}px`;
        }
        // get selectedElement:
        const selectedElement = this.fabricCanvas.getActiveObject() as IFabricCustomSelectable;
        const zoomFactor = this.fabricCanvas.getZoom();

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
        if (isIOS) {
            this.setvisualViewPortOffset();
        }
        if (selectedElement && zoomFactor) {
            this.focusOnElement(selectedElement.id);
        } else {
            // Reset viewport to center the content
            this.displayImageFullCanvas();
        }

        // Re-render the canvas
        this.fabricCanvas.renderAll();
    }

    /**
     * center on visualViewport
     */
    setvisualViewPortOffset(): void {
        if (!this.fabricCanvas) {
            console.warn('Canvas not initialized');
            return;
        }
        if (!window.visualViewport) {
            console.warn('No visualViewport available');
            return;
        }
        const { height } = window.visualViewport;
        const canvasElement = this.fabricCanvas.getElement();
        if (!canvasElement) return;
        const outerCanvasContainer = document.getElementsByClassName('canvas-container')[0] as HTMLElement;
        if (!outerCanvasContainer) return;

        // The offset below only makes sense while the keyboard is open. On close, the viewport height is
        // back to its initial value: always reset the margin, whatever the scroll position left by the
        // keyboard animation (the previous rect.top based heuristic kept a stale margin in that case).
        const isKeyboardOpen = this.initialViewportHeight > 0 && height < this.initialViewportHeight - KEYBOARD_MIN_HEIGHT;
        if (!isKeyboardOpen) {
            outerCanvasContainer.style.setProperty('margin-top', `${0}px`);
            return;
        }

        const rect = canvasElement.getBoundingClientRect();
        const topOffset = rect.top + height / 2;
        if (topOffset < 450) {
            if (!canvasElement.style.top || canvasElement.style.top === '0px') {
                // outerCanvasContainer.classList.add('centered-canvas');
                outerCanvasContainer.style.setProperty('margin-top', `${topOffset}px`);
            }
        } else {
            outerCanvasContainer.style.setProperty('margin-top', `${0}px`);
        }
    }
}

export { WFabricWorld };
